import React, { startTransition, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SaveButton, InputText, CancelButton, ModalComponent, trans } from "@comps/components"
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import { getPermissionsByRoleReq, reorderModulesReq, updateRolePermissionsReq } from "../requests";
import { Commons, Const, Validations } from "../../../constants";
import './../../../style.css'
import './../role-permission.css'
import ManageModules from "./ManageModules";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { masterTooltips } from "../../../../common/masterTooltips";

const langKey = Const["roles"].lngKey

const fieldLabel = makeFieldLabel(masterTooltips.role, { namespace: "role", label: (key) => trans(`${langKey}.${key}`) });
const formName = "updateRole";

const UpdateRole = (props) => {
  const [rolePermissions, setRolePermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [expandedKeys, setExpandedKeys] = useState({})
  const [showModuleManager, setShowModuleManager] = useState(false)
  const [organizing, setOrganizing] = useState(false)
  const [permissionValues, setPermissionValues] = useState({})
  const [reordering, setReordering] = useState(false)
  const [form] = Form.useForm();
  const deferredSearchValue = useDeferredValue(searchValue);
  const deferredPermissionValues = useDeferredValue(permissionValues);
  useEffect(() => {
    getPermissions()
    // eslint-disable-next-line
  }, []);

  const getPermissions = () => {
    makeRequestStateless(getPermissionsByRoleReq, props.roleId, onGetPermissions, onErrorGettingPermissions);
  }
  const onErrorGettingPermissions = (err, res) => {
    setLoading(false)
    message.error(res.response.data.message);
  }
  const onGetPermissions = (response) => {
    const sortedResponse = sortModuleTree(response)
    const initialValues = getInitialPermissionValues(sortedResponse)
    setRolePermissions(sortedResponse)
    setExpandedKeys({})
    setPermissionValues(initialValues)
    form.setFieldsValue({
      name: props.record.name,
      ...initialValues,
    })
    setLoading(false)
  }

  const onSubmit = (data) => {
    const payload = {
      permissions: {
        ...form.getFieldsValue(true),
        ...data,
      },
    };
    payload.id = props.record.id
    setSaving(true)
    makeRequest(setSaving, updateRolePermissionsReq, payload, onPermissionsUpdated, onError);
  }

  const onPermissionsUpdated = (data, res) => {
    if (res.code === 200) {
      message.success("Record Updated");
      props.onCompleted(data);
      setSaving(false)
      return;
    }
    setSaving(false)
    message.error(res.response.data.message);
  }

  const onError = (err, res) => {
    setSaving(false)
    message.error(res.response.data.message);
  }

  const getActionName = (action) => {
    const str = action.description || action.name
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const filteredPermissions = useMemo(
    () => filterPermissionsTree(rolePermissions, deferredSearchValue),
    [rolePermissions, deferredSearchValue]
  );

  const permissionStats = useMemo(
    () => getPermissionStats(rolePermissions, deferredPermissionValues),
    [rolePermissions, deferredPermissionValues]
  );

  const visibleStats = useMemo(
    () => getPermissionStats(filteredPermissions, deferredPermissionValues),
    [filteredPermissions, deferredPermissionValues]
  );

  const moduleStatsMap = useMemo(
    () => buildPermissionStatsMap(rolePermissions, deferredPermissionValues),
    [rolePermissions, deferredPermissionValues]
  );

  const onToggleModule = (module, checked) => {
    const valuesToSet = {};
    collectModulePermissionValues(module, checked, valuesToSet);
    form.setFieldsValue(valuesToSet);
    startTransition(() => {
      setPermissionValues((prev) => ({
        ...prev,
        ...valuesToSet,
      }));
    });
  };

  const onToggleAllVisible = (checked) => {
    const valuesToSet = {};
    filteredPermissions.forEach((module) => collectModulePermissionValues(module, checked, valuesToSet));
    form.setFieldsValue(valuesToSet);
    startTransition(() => {
      setPermissionValues((prev) => ({
        ...prev,
        ...valuesToSet,
      }));
    });
  };

  const onToggleExpanded = (moduleKey) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  const onExpandAll = (expand) => {
    if (!expand) {
      setExpandedKeys({});
      return;
    }
    setExpandedKeys(getAllExpandedKeys(rolePermissions));
  };

  const onModuleCreated = () => {
    setShowModuleManager(false);
    setLoading(true);
    getPermissions();
  };

  const onMoveModule = async (sourceId, targetId, position) => {
    if (sourceId === targetId || reordering) {
      return;
    }

    const nextTree = moveModuleInTree(rolePermissions, sourceId, targetId, position);
    if (!nextTree) {
      message.warning("A module cannot be moved inside itself or one of its children.");
      return;
    }

    const previousTree = rolePermissions;
    setRolePermissions(nextTree);
    setExpandedKeys((prev) => ({ ...prev, [String(targetId)]: true }));
    setReordering(true);
    try {
      await reorderModulesReq(flattenModuleOrder(nextTree));
      message.success("Module hierarchy updated");
    } catch (error) {
      setRolePermissions(previousTree);
      message.error(error?.response?.data?.message || "Unable to update module hierarchy");
    } finally {
      setReordering(false);
    }
  };

  const onKeyboardMove = (moduleId, direction) => {
    const location = findModuleLocation(rolePermissions, moduleId);
    if (!location) {
      return;
    }

    if (direction === "up" && location.index > 0) {
      onMoveModule(moduleId, location.siblings[location.index - 1].id, "before");
    } else if (direction === "down" && location.index < location.siblings.length - 1) {
      onMoveModule(moduleId, location.siblings[location.index + 1].id, "after");
    } else if (direction === "outdent" && location.parent) {
      onMoveModule(moduleId, location.parent.id, "after");
    }
  };

  const renderModule = (module, level = 0, parentIndex = "") => {
    const moduleKey = getModuleKey(module, parentIndex);

    return (
      <PermissionModuleCard
        key={ moduleKey }
        module={ module }
        moduleKey={ moduleKey }
        level={ level }
        expanded={ deferredSearchValue ? true : !!expandedKeys[moduleKey] }
        forceExpanded={ !!deferredSearchValue }
        statsMap={ moduleStatsMap }
        disabled={ props.disabled }
        organizing={ organizing }
        reordering={ reordering }
        getActionName={ getActionName }
        onToggleExpanded={ onToggleExpanded }
        onToggleModule={ onToggleModule }
        onMoveModule={ onMoveModule }
        onKeyboardMove={ onKeyboardMove }
        renderModule={ renderModule }
      />
    );
  };

  // ------------------------------------
  // Start footer buttons array
  // ------------------------------------
  const footer = [
    !props.disabled &&
    <SaveButton loading={ saving } form={ formName } key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />
  ];
  /*  */
  // ------------------------------------
  // End footer buttons array
  // ------------------------------------
  return (
    <ModalComponent top={ 20 } width={ 1200 } maskClosable={ false }
      mainTitle={ trans(Commons.update) } subTitle={ trans(`${langKey}.moduleName`) }
      open={ true } footer={ footer } onCancel={ () => props.onCompleted(false) } disabled={ props.disabled }>

      <Form
        { ...formLayout }
        form={ form }
        name={ formName }
        onFinish={ onSubmit }
        onValuesChange={ (changedValues) => {
          if (!changedValues || Object.keys(changedValues).length === 0) {
            return;
          }

          startTransition(() => {
            setPermissionValues((prev) => ({
              ...prev,
              ...changedValues,
            }));
          });
        } }
        disabled={ props.disabled }
        className="update-form"
      >

        <Form.Item name="name" rules={ rules.name } label={ fieldLabel("name", trans(Commons.name)) }
          className="da-mb-16"

        >
          <InputText />
        </Form.Item>

        <Spin spinning={ loading }>
          <div className="role-permission-toolbar">
            <div>
              <Typography.Title level={ 5 } className="role-permission-toolbar__title">
                Permissions
              </Typography.Title>
              <Typography.Text type="secondary">
                { permissionStats.enabled } of { permissionStats.total } actions enabled across { permissionStats.modules } modules
              </Typography.Text>
            </div>
            <Space wrap>
              <Badge color="#1677ff" text={ `${visibleStats.modules} visible modules` } />
              <Button icon={ <ReloadOutlined /> } onClick={ getPermissions }>
                Refresh
              </Button>
              <Button
                type={ organizing ? "primary" : "default" }
                icon={ <HolderOutlined /> }
                onClick={ () => {
                  setOrganizing((prev) => !prev);
                  setExpandedKeys(getAllExpandedKeys(rolePermissions));
                } }
                disabled={ props.disabled || !!deferredSearchValue }
              >
                { organizing ? "Finish organizing" : "Organize modules" }
              </Button>
              <Button icon={ <PlusOutlined /> } onClick={ () => setShowModuleManager(true) } disabled={ props.disabled }>
                Add module
              </Button>
            </Space>
          </div>

          <div className="role-permission-summary">
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Enabled</span>
              <strong>{ permissionStats.enabled }</strong>
            </div>
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Available actions</span>
              <strong>{ permissionStats.total }</strong>
            </div>
            <div className="role-permission-summary__card">
              <span className="role-permission-summary__label">Coverage</span>
              <strong>{ permissionStats.total ? `${Math.round((permissionStats.enabled / permissionStats.total) * 100)}%` : "0%" }</strong>
            </div>
          </div>

          { organizing ? (
            <div className="module-organizer-help" role="status">
              <HolderOutlined />
              <div>
                <Typography.Text strong>Drag modules to reorder or reparent them</Typography.Text>
                <Typography.Text type="secondary">
                  Drop near the top or bottom edge to reorder. Drop in the center to make it a child.
                </Typography.Text>
              </div>
              { reordering ? <Spin size="small" /> : null }
            </div>
          ) : null }

          <div className="role-permission-filters">
            <Input
              allowClear
              value={ searchValue }
              onChange={ (event) => setSearchValue(event.target.value) }
              prefix={ <SearchOutlined /> }
              placeholder="Search modules or actions"
              className="role-permission-filters__search"
            />
            <Space wrap>
              <Button htmlType="button" size="small" onClick={ () => onExpandAll(true) }>
                Expand all
              </Button>
              <Button htmlType="button" size="small" onClick={ () => onExpandAll(false) }>
                Collapse all
              </Button>
              <Button htmlType="button" size="small" onClick={ () => onToggleAllVisible(true) } disabled={ props.disabled || visibleStats.total === 0 }>
                Enable visible
              </Button>
              <Button htmlType="button" size="small" onClick={ () => onToggleAllVisible(false) } disabled={ props.disabled || visibleStats.total === 0 }>
                Clear visible
              </Button>
            </Space>
          </div>

          { filteredPermissions.length > 0 ? (
            <DndProvider backend={ HTML5Backend }>
              <div className={ `role-permission-tree${organizing ? " role-permission-tree--organizing" : ""}` }>
                { filteredPermissions.map((module, index) => renderModule(module, 0, `${index + 1}`)) }
              </div>
            </DndProvider>
          ) : (
            <Empty description="No modules match your search." className="role-permission-empty" />
          ) }
        </Spin>
      </Form>

      { showModuleManager ? (
        <ManageModules
          onCompleted={ onModuleCreated }
          onCancel={ () => setShowModuleManager(false) }
        />
      ) : null }
    </ModalComponent>
  )
}

const PermissionModuleCard = ({
  module,
  moduleKey,
  level,
  expanded,
  forceExpanded,
  statsMap,
  disabled,
  organizing,
  reordering,
  getActionName,
  onToggleExpanded,
  onToggleModule,
  onMoveModule,
  onKeyboardMove,
  renderModule,
}) => {
  const cardRef = useRef(null);
  const handleRef = useRef(null);
  const [dropPosition, setDropPosition] = useState("inside");
  const children = module.modules || [];
  const hasChildren = children.length > 0;
  const moduleStats = statsMap[module.id] || EMPTY_STATS;
  const progressPercent = moduleStats.total ? Math.round((moduleStats.enabled / moduleStats.total) * 100) : 0;
  const title = prettifyPermissionText(module.description || module.name || "Unnamed module");

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: MODULE_DRAG_TYPE,
    item: { id: module.id },
    canDrag: organizing && !disabled && !reordering,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [module.id, organizing, disabled, reordering]);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: MODULE_DRAG_TYPE,
    canDrop: (item) => organizing && item.id !== module.id,
    hover: (_, monitor) => {
      if (!cardRef.current) {
        return;
      }

      const bounds = cardRef.current.getBoundingClientRect();
      const pointer = monitor.getClientOffset();
      if (!pointer) {
        return;
      }

      const ratio = (pointer.y - bounds.top) / bounds.height;
      setDropPosition(ratio < 0.25 ? "before" : ratio > 0.75 ? "after" : "inside");
    },
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return;
      }
      onMoveModule(item.id, module.id, dropPosition);
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  }), [module.id, organizing, dropPosition, onMoveModule]);

  preview(drop(cardRef));
  drag(handleRef);

  return (
    <div
      ref={ cardRef }
      className={ [
        "role-permission-card",
        organizing ? "role-permission-card--organizing" : "",
        isDragging ? "role-permission-card--dragging" : "",
        isOver ? `role-permission-card--drop-${dropPosition}` : "",
      ].filter(Boolean).join(" ") }
      style={ { marginLeft: level > 0 ? `${Math.min(level * 20, 60)}px` : 0 } }
    >
      <div className="role-permission-card__header">
        <div className="role-permission-card__title-group">
          { organizing ? (
            <button
              ref={ handleRef }
              type="button"
              className="role-permission-card__drag-handle"
              aria-label={ `Drag ${title}` }
              title="Drag to reorder or move this module"
            >
              <HolderOutlined />
            </button>
          ) : null }
          { hasChildren && !forceExpanded ? (
            <Button
              type="text"
              className="role-permission-card__toggle"
              icon={ expanded ? <UpOutlined /> : <DownOutlined /> }
              onClick={ () => onToggleExpanded(moduleKey) }
              aria-label={ `${expanded ? "Collapse" : "Expand"} ${title}` }
              aria-expanded={ expanded }
            />
          ) : (
            <span className="role-permission-card__toggle-spacer" />
          ) }
          <div>
            <div className="role-permission-card__title-row">
              <Typography.Text strong>{ title }</Typography.Text>
              <Tag bordered={ false }>{ moduleStats.enabled }/{ moduleStats.total }</Tag>
              { organizing ? <Tag color="blue">{ level === 0 ? "Top level" : `Level ${level + 1}` }</Tag> : null }
            </div>
            <Typography.Text type="secondary" className="role-permission-card__meta">
              { module.name || title }
            </Typography.Text>
          </div>
        </div>
        <div className="role-permission-card__tools">
          { organizing ? (
            <Space size={ 4 } className="role-permission-card__move-actions">
              <Button
                htmlType="button"
                icon={ <ArrowUpOutlined /> }
                onClick={ () => onKeyboardMove(module.id, "up") }
                aria-label={ `Move ${title} up` }
                title="Move up"
              />
              <Button
                htmlType="button"
                icon={ <ArrowDownOutlined /> }
                onClick={ () => onKeyboardMove(module.id, "down") }
                aria-label={ `Move ${title} down` }
                title="Move down"
              />
              { level > 0 ? (
                <Button htmlType="button" onClick={ () => onKeyboardMove(module.id, "outdent") }>
                  Move out
                </Button>
              ) : null }
            </Space>
          ) : null }
          <Progress
            percent={ progressPercent }
            size="small"
            showInfo={ false }
            className="role-permission-card__progress"
          />
          <Space size={ 8 } wrap>
            <Button htmlType="button" size="small" onClick={ () => onToggleModule(module, true) } disabled={ disabled || moduleStats.total === 0 }>
              Select all
            </Button>
            <Button htmlType="button" size="small" onClick={ () => onToggleModule(module, false) } disabled={ disabled || moduleStats.total === 0 }>
              Clear
            </Button>
          </Space>
        </div>
      </div>

      { module.actions && module.actions.length > 0 ? (
        <div className="role-permission-card__actions">
          { module.actions.map((action) => (
            <Form.Item
              key={ `${moduleKey}-${action.role_permission_id}` }
              name={ String(action.role_permission_id) }
              valuePropName="checked"
              className="role-permission-card__action-item"
            >
              <Checkbox className="role-permission-card__action-checkbox">
                { getActionName(action) }
              </Checkbox>
            </Form.Item>
          )) }
        </div>
      ) : (
        <div className="role-permission-card__empty">No actions configured for this module.</div>
      ) }

      { hasChildren && expanded ? (
        <div className="role-permission-card__children">
          { children.map((child, index) => renderModule(child, level + 1, `${moduleKey}-${index + 1}`)) }
        </div>
      ) : null }
    </div>
  );
};

export default UpdateRole

const rules = {
  name: [
    { required: true, message: trans(Validations.name) },
  ],
};

const formLayout = {
  // layout: "horizontal",
  labelCol: { span: 6 },
  wrapperCol: { span: 14, offset: 0 },
  labelAlign: "left"
}

const prettifyPermissionText = (value = "") => {
  return String(value)
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getModuleKey = (module, fallback = "") => {
  return `${module.id || module.name || fallback}`;
};

const getActionCheckedValue = (action, formValues = {}) => {
  const value = formValues[action.role_permission_id];
  return typeof value === "boolean" ? value : !!action.enable;
};

const getSingleModuleStats = (module, formValues = {}) => {
  let total = 0;
  let enabled = 0;
  let modules = 1;

  (module.actions || []).forEach((action) => {
    total += 1;
    if (getActionCheckedValue(action, formValues)) {
      enabled += 1;
    }
  });

  (module.modules || []).forEach((child) => {
    const childStats = getSingleModuleStats(child, formValues);
    total += childStats.total;
    enabled += childStats.enabled;
    modules += childStats.modules;
  });

  return { total, enabled, modules };
};

const getPermissionStats = (modules = [], formValues = {}) => {
  return modules.reduce((acc, module) => {
    const moduleStats = getSingleModuleStats(module, formValues);
    acc.modules += moduleStats.modules;
    acc.total += moduleStats.total;
    acc.enabled += moduleStats.enabled;
    return acc;
  }, { modules: 0, total: 0, enabled: 0 });
};

const buildPermissionStatsMap = (modules = [], formValues = {}, result = {}) => {
  (modules || []).forEach((module) => {
    result[module.id] = getSingleModuleStats(module, formValues);
    if (module.modules && module.modules.length > 0) {
      buildPermissionStatsMap(module.modules, formValues, result);
    }
  });

  return result;
};

const collectModulePermissionValues = (module, checked, valuesToSet = {}) => {
  (module.actions || []).forEach((action) => {
    valuesToSet[String(action.role_permission_id)] = checked;
  });

  (module.modules || []).forEach((child) => collectModulePermissionValues(child, checked, valuesToSet));
  return valuesToSet;
};

const getAllExpandedKeys = (modules = {}, keys = {}) => {
  (modules || []).forEach((module, index) => {
    const moduleKey = getModuleKey(module, `${index + 1}`);
    keys[moduleKey] = true;
    if (module.modules && module.modules.length > 0) {
      getAllExpandedKeys(module.modules, keys);
    }
  });
  return keys;
};

const filterPermissionsTree = (modules = [], searchValue = "") => {
  const query = searchValue.trim().toLowerCase();
  if (!query) {
    return modules;
  }

  return modules.reduce((acc, module) => {
    const title = `${module.name || ""} ${module.description || ""}`.toLowerCase();
    const matchingActions = (module.actions || []).filter((action) =>
      `${action.name || ""} ${action.description || ""}`.toLowerCase().includes(query)
    );
    const matchingChildren = filterPermissionsTree(module.modules || [], query);

    if (title.includes(query) || matchingActions.length > 0 || matchingChildren.length > 0) {
      acc.push({
        ...module,
        actions: title.includes(query) ? module.actions : matchingActions,
        modules: matchingChildren,
      });
    }

    return acc;
  }, []);
};

const sortModulesByOrder = (modules = []) => {
  return [...(modules || [])].sort((first, second) => {
    const firstOrder = Number(first.order || 0);
    const secondOrder = Number(second.order || 0);

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return prettifyPermissionText(first.description || first.name || "").localeCompare(
      prettifyPermissionText(second.description || second.name || "")
    );
  });
};

const sortModuleTree = (modules = []) => {
  return sortModulesByOrder(modules).map((module) => ({
    ...module,
    modules: sortModuleTree(module.modules || []),
  }));
};

const getInitialPermissionValues = (modules = [], values = {}) => {
  (modules || []).forEach((module) => {
    (module.actions || []).forEach((action) => {
      values[String(action.role_permission_id)] = !!action.enable;
    });

    if (module.modules && module.modules.length > 0) {
      getInitialPermissionValues(module.modules, values);
    }
  });

  return values;
};

const EMPTY_STATS = {
  total: 0,
  enabled: 0,
  modules: 0,
};

const MODULE_DRAG_TYPE = "role-permission-module";

const treeContainsModule = (module, moduleId) => {
  if (module.id === moduleId) {
    return true;
  }

  return (module.modules || []).some((child) => treeContainsModule(child, moduleId));
};

const removeModuleFromTree = (modules, moduleId) => {
  let removed = null;
  const nextModules = [];

  modules.forEach((module) => {
    if (module.id === moduleId) {
      removed = module;
      return;
    }

    const [nextChildren, childRemoved] = removeModuleFromTree(module.modules || [], moduleId);
    if (childRemoved) {
      removed = childRemoved;
      nextModules.push({ ...module, modules: nextChildren });
    } else {
      nextModules.push(module);
    }
  });

  return [nextModules, removed];
};

const insertModuleInTree = (modules, movedModule, targetId, position) => {
  const targetIndex = modules.findIndex((module) => module.id === targetId);
  if (targetIndex >= 0 && position !== "inside") {
    const nextModules = [...modules];
    nextModules.splice(position === "before" ? targetIndex : targetIndex + 1, 0, movedModule);
    return [nextModules, true];
  }

  let inserted = false;
  const nextModules = modules.map((module) => {
    if (module.id === targetId && position === "inside") {
      inserted = true;
      return { ...module, modules: [...(module.modules || []), movedModule] };
    }

    const [nextChildren, childInserted] = insertModuleInTree(
      module.modules || [],
      movedModule,
      targetId,
      position
    );
    if (childInserted) {
      inserted = true;
      return { ...module, modules: nextChildren };
    }

    return module;
  });

  return [nextModules, inserted];
};

const moveModuleInTree = (modules, sourceId, targetId, position) => {
  const source = findModuleInTree(modules, sourceId);
  if (!source || treeContainsModule(source, targetId)) {
    return null;
  }

  const [treeWithoutSource, movedModule] = removeModuleFromTree(modules, sourceId);
  if (!movedModule) {
    return null;
  }

  const [nextTree, inserted] = insertModuleInTree(treeWithoutSource, movedModule, targetId, position);
  return inserted ? nextTree : null;
};

const findModuleInTree = (modules, moduleId) => {
  for (const module of modules) {
    if (module.id === moduleId) {
      return module;
    }
    const child = findModuleInTree(module.modules || [], moduleId);
    if (child) {
      return child;
    }
  }
  return null;
};

const findModuleLocation = (modules, moduleId, parent = null) => {
  for (let index = 0; index < modules.length; index += 1) {
    const module = modules[index];
    if (module.id === moduleId) {
      return { module, parent, siblings: modules, index };
    }

    const childLocation = findModuleLocation(module.modules || [], moduleId, module);
    if (childLocation) {
      return childLocation;
    }
  }

  return null;
};

const flattenModuleOrder = (modules, parentId = null, result = []) => {
  modules.forEach((module, index) => {
    result.push({
      id: module.id,
      parent_id: parentId,
      order: index + 1,
    });
    flattenModuleOrder(module.modules || [], module.id, result);
  });

  return result;
};
